import React, {useContext, useEffect, useState} from 'react';
import {useRouter} from 'next/router';
import BookingService from '../../services/booking.service';
import TNHContext from '@contexts/TNHContext';
import {getLocaleContent} from '@helper/locale-content.js';
import BookingSuccess from '../Home/BookingSuccess';
import {useMasterDataStore} from '@stores/useMasterDataStore';
import {FormField, FormErrors} from '../Form';
import UserService from '../../services/user.service';
import ExpertiseService from '@services/expertise.service';
import moment from 'moment';

export default function NewsBookingForm() {
    const {companyId} = useContext(TNHContext);
    const {locale} = useRouter();

    // Lấy dữ liệu từ store
    const businessData = useMasterDataStore(s => s.businessData);
    const departments = useMasterDataStore(s => s.departments);
    const fetchAllMasterData = useMasterDataStore(s => s.fetchAllMasterData);

    // State quản lý danh sách bác sĩ được load theo chuyên khoa
    const [doctors, setDoctors] = useState([]);
    const [expertises, setExpertises] = useState([]);

    // State quản lý form
    const [form, setForm] = useState({
        name: '',
        phone: '',
        email: '',
        hospital: '',
        department: '',
        doctor: '',
        department_name: '',
        doctor_name: '',
        expertises: '',
        time: '',
        symptoms: '',
    });

    // State quản lý modal thành công
    const [showSuccess, setShowSuccess] = useState(false);
    const [bookingCode, setBookingCode] = useState('');

    // State quản lý validation errors
    const [errors, setErrors] = useState({});

    // Lấy dữ liệu master khi component mount
    useEffect(() => {
        if (!locale) return;
        fetchAllMasterData(locale, companyId);
        const expertiseService = new ExpertiseService();
        expertiseService
            .getListHome({locale: locale})
            .then(response => {
                setExpertises(response);
                console.log(response);
            })
            .catch(error => {
                console.error('Error fetching expertise data:', error);
            });
    }, [locale, companyId, fetchAllMasterData]);

    // Load bác sĩ theo chuyên khoa được chọn (giống logic trong PopupAppointment)
    useEffect(() => {
        // if (!form.department || !form.hospital) {
        //     setDoctors([]);
        //     return;
        // }

        console.log('[FormRegister] Load bác sĩ theo chuyên khoa:', form.department);
        const userService = new UserService();
        userService
            .getUserOptions({
                businessId: form.hospital,
                locale,
                departmentId: form.department,
                expertiseId: form.expertises,
            })
            .then(response => {
                const doctorList = response?.data || response || [];
                console.log('[FormRegister] Đã load bác sĩ:', doctorList.length, 'bác sĩ');
                setDoctors(doctorList);
            })
            .catch(error => {
                console.error('[FormRegister] Lỗi khi load bác sĩ:', error);
                setDoctors([]);
            });
    }, [locale, form.department, form.hospital, form.expertises]);

    // Xử lý thay đổi input
    const handleChange = e => {
        const {name, value} = e.target;
        const text = e.target.selectedOptions?.[0]?.text || '';
        if (name === 'phone') {
            const digits = value.replace(/\D/g, '').slice(0, 11); // keep only numbers, max 11
            setForm(prev => ({...prev, phone: digits}));
            return;
        }

        // Nếu thay đổi department, clear doctor để tránh chọn bác sĩ không thuộc chuyên khoa mới
        if (name === 'department') {
            console.log('Thay đổi chuyên khoa ' + value + text);
            setForm(prev => ({
                ...prev,
                department: value,
                department_name: text,
                doctor: '', // Clear doctor khi đổi chuyên khoa
                expertises: '',
                doctor_name: '',
            }));
        } else if (name === 'doctor') {
            setForm(prev => ({...prev, doctor: value, doctor_name: text}));
        } else {
            setForm(prev => ({...prev, [name]: value}));
        }
        // Xóa lỗi khi user nhập lại
        if (errors[name]) {
            setErrors(prev => ({...prev, [name]: ''}));
        }
    };

    const handlePhoneKeyDown = e => {
        const allowed = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End', 'Escape', 'Enter'];
        if (allowed.includes(e.key) || e.ctrlKey || e.metaKey) {
            return;
        }
        if (!/[0-9]/.test(e.key)) {
            e.preventDefault();
        }
    };

    const handleTimeKeyDown = e => {
        if (e.key === 'Tab') return;
        e.preventDefault();
    };

    const handleTimeFocus = e => {
        try {
            e.target.showPicker?.();
        } catch (err) {
            console.warn('showPicker not supported:', err);
        }
    };

    const handleTimeClick = e => {
        try {
            e.target.showPicker?.();
        } catch (err) {
            console.warn('showPicker not supported:', err);
        }
    };

    // Xử lý submit form
    const handleSubmit = async e => {
        e.preventDefault();
        e.stopPropagation();

        setErrors({});
        const errs = {};

        if (!form.name.trim()) errs.name = getLocaleContent(locale, 'page.booking-form.validation.full-name-required');
        if (!form.phone.trim()) errs.phone = getLocaleContent(locale, 'page.booking-form.validation.phone-required');
        else if (!/^[0-9]{10,11}$/.test(form.phone.trim()))
            errs.phone = getLocaleContent(locale, 'page.booking-form.validation.phone-matches');

        if (!form.hospital) errs.hospital = getLocaleContent(locale, 'page.booking-form.validation.hospital-required');

        if (!form.time) errs.time = getLocaleContent(locale, 'page.booking-form.validation.time-required');
        else {
            const m = moment(form.time);
            if (!m.isValid()) errs.time = getLocaleContent(locale, 'page.booking-form.validation.time-matches');
            else if (m.isBefore(moment()))
                errs.time = getLocaleContent(locale, 'page.booking-form.validation.time-past');
        }

        if (Object.keys(errs).length > 0) {
            setErrors(errs);
            return;
        }

        // Lấy business_code từ businessData
        const selectedBusiness = businessData.find(b => b.business_id === form.hospital);
        const business_code = selectedBusiness ? selectedBusiness.business_code : '';

        const bookingData = {
            ...form,
            business_code: business_code,
            time: `${form.time}:00`,
        };

        const bookingService = new BookingService();
        bookingService
            .makeABooking(bookingData)
            .then(response => {
                if (response.code === 0) {
                    setBookingCode(response?.Extent?.MaDK || '');
                    setShowSuccess(true);
                    setForm({
                        name: '',
                        phone: '',
                        email: '',
                        hospital: '',
                        department: '',
                        doctor: '',
                        department_name: '',
                        doctor_name: '',
                        expertises: '',
                        time: '',
                        symptoms: '',
                    });
                } else {
                    alert(getLocaleContent(locale, 'page.booking-form.error-message'));
                }
            })
            .catch(error => {
                alert(getLocaleContent(locale, 'page.booking-form.error-message'));
            });
    };

    return (
        <>
            <form onSubmit={handleSubmit} noValidate>
                {/* Hiển thị tất cả validation errors */}
                <FormErrors errors={errors} />
                <div className="info">
                    <span className="required-label">{getLocaleContent(locale, 'page.booking-form.full-name')}</span>
                    <FormField error={errors.name} preserveStyle>
                        <input
                            type="text"
                            name="name"
                            value={form.name}
                            onChange={handleChange}
                            placeholder={getLocaleContent(locale, 'page.booking-form.full-name-placeholder')}
                            style={errors.name ? {borderColor: '#ff4d4f'} : {}}
                        />
                    </FormField>
                </div>

                <div className="info">
                    <span className="required-label">{getLocaleContent(locale, 'page.booking-form.phone')}</span>
                    <FormField error={errors.phone} preserveStyle>
                        <input
                            type="tel"
                            name="phone"
                            value={form.phone}
                            onChange={handleChange}
                            onKeyDown={handlePhoneKeyDown}
                            inputMode="numeric"
                            pattern="[0-9]*"
                            maxLength={11}
                            placeholder={getLocaleContent(locale, 'page.booking-form.phone-placeholder')}
                            style={errors.phone ? {borderColor: '#ff4d4f'} : {}}
                        />
                    </FormField>
                </div>

                <div className="info">
                    <span className="required-label">{getLocaleContent(locale, 'page.booking-form.hospital')}</span>
                    <FormField error={errors.hospital} preserveStyle>
                        <select
                            name="hospital"
                            value={form.hospital}
                            onChange={handleChange}
                            style={errors.hospital ? {borderColor: '#ff4d4f'} : {}}>
                            <option value="">{getLocaleContent(locale, 'page.booking-form.choose-hospital')}</option>
                            {businessData?.map(business => (
                                <option key={business.business_id} value={business.business_id}>
                                    {business.business_name}
                                </option>
                            ))}
                        </select>
                    </FormField>
                </div>

                {/* <div className="info">
                    <span>{getLocaleContent(locale, 'page.booking-form.department')}</span>
                    <FormField preserveStyle>
                        <select name="department" value={form.department} onChange={handleChange}>
                            <option value="">{getLocaleContent(locale, 'page.booking-form.choose-department')}</option>
                            {departments?.map(department => (
                                <option key={department.department_id} value={department.department_id}>
                                    {department.department_name}
                                </option>
                            ))}
                        </select>
                    </FormField>
                </div> */}

                <div className="info">
                    <p htmlFor="expertise">{getLocaleContent(locale, 'page.booking-form.expertise')}</p>
                    <div className="select-wrapper">
                        <select
                            id="expertises"
                            name="expertises"
                            value={form.expertises}
                            onChange={handleChange}
                            // disabled={hasAllDoctorInfo}
                            // style={hasAllDoctorInfo ? {opacity: 0.6, cursor: 'not-allowed'} : {}}
                        >
                            <option value="">{getLocaleContent(locale, 'page.booking-form.choose-expertise')}</option>
                            {expertises.map(expertise => (
                                <option key={expertise.expertise_id} value={expertise.expertise_id}>
                                    {expertise.expertise_name}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="info">
                    <span>{getLocaleContent(locale, 'page.booking-form.doctor')}</span>
                    <FormField preserveStyle>
                        <select name="doctor" value={form.doctor} onChange={handleChange}>
                            <option value="">{getLocaleContent(locale, 'page.booking-form.choose-doctor')}</option>
                            {doctors?.map(doctor => (
                                <option key={doctor.user_id} value={doctor.user_name}>
                                    {doctor.full_name}
                                </option>
                            ))}
                        </select>
                    </FormField>
                </div>

                <div className="info">
                    <span className="required-label">{getLocaleContent(locale, 'page.booking-form.time')}</span>
                    <FormField error={errors.time} preserveStyle>
                        <input
                            type="datetime-local"
                            name="time"
                            value={form.time}
                            onChange={handleChange}
                            onKeyDown={handleTimeKeyDown}
                            onFocus={handleTimeFocus}
                            onClick={handleTimeClick}
                            min={moment().format('YYYY-MM-DDTHH:mm')}
                            style={errors.time ? {borderColor: '#ff4d4f', cursor: 'pointer'} : {cursor: 'pointer'}}
                        />
                    </FormField>
                </div>

                <div className="info">
                    <span>{getLocaleContent(locale, 'page.booking-form.symptoms-label')}</span>
                    <FormField error={errors.symptoms} preserveStyle>
                        <textarea
                            name="symptoms"
                            value={form.symptoms}
                            onChange={handleChange}
                            placeholder={getLocaleContent(locale, 'page.booking-form.symptoms-placeholder')}
                            style={errors.symptoms ? {borderColor: '#ff4d4f'} : {}}
                        />
                    </FormField>
                </div>

                <button type="submit" className="submit-btn">
                    {getLocaleContent(locale, 'page.booking-form.send')}{' '}
                    <img src="/bw_image/arrow-long-right-whilte.png" alt="Submit" />
                </button>
            </form>

            <BookingSuccess
                open={showSuccess}
                bookingCode={bookingCode}
                onClose={() => {
                    setShowSuccess(false);
                    setBookingCode('');
                }}
            />
        </>
    );
}
